import React, { useState } from 'react';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import supabase from '../supabase/supabase';
import { useGuardian } from '../contexts/GuardianContext';

const { FiMessageSquare, FiMail, FiPhone, FiFileText, FiClock, FiPlus, FiSave, FiX } = FiIcons;

function CommunicationHistory({ logs, contacts, onLogAdded }) {
  const { getActiveUserId } = useGuardian();
  const [showManualLog, setShowManualLog] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    contact_id: '',
    type: 'note',
    outcome: '',
    noteContent: ''
  });

  const getIconForType = (type) => {
    switch (type) {
      case 'email': return <SafeIcon icon={FiMail} className="w-4 h-4 text-blue-500" />;
      case 'phone_call': return <SafeIcon icon={FiPhone} className="w-4 h-4 text-green-500" />;
      default: return <SafeIcon icon={FiFileText} className="w-4 h-4 text-purple-500" />;
    }
  };

  const handleManualLog = async () => {
    if (!form.contact_id || !form.noteContent) {
      alert('Please select a contact and add details.');
      return;
    }
    
    try {
      setSaving(true);
      const activeUserId = getActiveUserId();
      
      const { error } = await supabase.from('communication_log').insert({
        user_id: activeUserId,
        contact_id: form.contact_id,
        type: form.type,
        outcome: form.outcome,
        details: { note: form.noteContent }
      });
      
      if (error) throw error;

      // Update last_contacted_at
      await supabase.from('contacts').update({
        last_contacted_at: new Date().toISOString()
      }).eq('id', form.contact_id);

      setShowManualLog(false);
      setForm({ contact_id: '', type: 'note', outcome: '', noteContent: '' });
      if(onLogAdded) onLogAdded();
    } catch (err) {
      console.error('Failed to log activity:', err);
      alert('Failed to save log.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center">
            <SafeIcon icon={FiClock} className="w-5 h-5 text-gray-700" />
          </div>
          <div>
            <h3 className="text-xl font-space-grotesk font-semibold text-secondary">Activity History</h3>
            <p className="text-sm text-gray-500">Record of your outreach and notes</p>
          </div>
        </div>
        <button 
          onClick={() => setShowManualLog(!showManualLog)}
          className="text-primary hover:bg-blue-50 px-3 py-2 rounded-lg transition-colors flex items-center space-x-2 text-sm font-medium"
        >
          <SafeIcon icon={showManualLog ? FiX : FiPlus} className="w-4 h-4" />
          <span>{showManualLog ? 'Cancel' : 'Log Activity'}</span>
        </button>
      </div>

      {showManualLog && (
        <div className="mb-8 p-4 bg-gray-50 border border-gray-200 rounded-lg space-y-4">
          <h4 className="font-semibold text-gray-800 text-sm">Manual Activity Log</h4>
          <div className="grid md:grid-cols-3 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Contact *</label>
              <select 
                value={form.contact_id} 
                onChange={(e) => setForm({...form, contact_id: e.target.value})}
                className="w-full px-3 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-primary outline-none"
              >
                <option value="">-- Select Contact --</option>
                {contacts.map(c => (
                  <option key={c.id} value={c.id}>{c.first_name} {c.last_name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Type</label>
              <select 
                value={form.type} 
                onChange={(e) => setForm({...form, type: e.target.value})}
                className="w-full px-3 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-primary outline-none"
              >
                <option value="note">General Note</option>
                <option value="phone_call">Phone Call</option>
                <option value="social_media">Social Media DM</option>
                <option value="in_person">In-Person Meeting</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Outcome</label>
              <input 
                type="text" 
                placeholder="e.g. Left Voicemail, Good Conversation"
                value={form.outcome} 
                onChange={(e) => setForm({...form, outcome: e.target.value})}
                className="w-full px-3 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-primary outline-none"
              />
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Details *</label>
            <textarea 
              value={form.noteContent} 
              onChange={(e) => setForm({...form, noteContent: e.target.value})}
              rows={2}
              className="w-full px-3 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-primary outline-none resize-none"
            />
          </div>
          <div className="flex justify-end">
            <button 
              onClick={handleManualLog} 
              disabled={saving}
              className="bg-gray-800 text-white px-4 py-2 rounded-lg hover:bg-gray-900 transition-colors text-sm flex items-center space-x-2"
            >
              <SafeIcon icon={FiSave} className="w-4 h-4" />
              <span>{saving ? 'Saving...' : 'Save Log'}</span>
            </button>
          </div>
        </div>
      )}

      {logs.length === 0 ? (
        <div className="text-center py-8 border-2 border-dashed border-gray-100 rounded-lg">
          <SafeIcon icon={FiMessageSquare} className="w-8 h-8 text-gray-300 mx-auto mb-2" />
          <p className="text-sm text-gray-500">No activity logged yet.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {logs.map((log) => {
            const contact = contacts.find(c => c.id === log.contact_id);
            return (
              <div key={log.id} className="flex space-x-4 p-4 border border-gray-100 rounded-lg hover:bg-gray-50 transition-colors">
                <div className="mt-1 flex-shrink-0">
                  <div className="w-8 h-8 rounded-full bg-white border border-gray-200 flex items-center justify-center shadow-sm">
                    {getIconForType(log.type)}
                  </div>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-sm font-semibold text-gray-800">
                        {log.type === 'email' ? 'Sent Email' : log.type === 'phone_call' ? 'Phone Call' : 'Added Note'}
                        {contact && <span className="text-gray-500 font-normal"> to {contact.first_name} {contact.last_name}</span>}
                      </p>
                      <p className="text-xs text-gray-500 mt-0.5">
                        {new Date(log.timestamp).toLocaleString()}
                      </p>
                    </div>
                    {log.outcome && (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800 capitalize">
                        {log.outcome}
                      </span>
                    )}
                  </div>
                  <div className="mt-2 text-sm text-gray-700 bg-white p-3 border border-gray-100 rounded-md">
                    {log.details.subject && <p className="font-semibold mb-1">Subject: {log.details.subject}</p>}
                    <p className="whitespace-pre-wrap">{log.details.body || log.details.note}</p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default CommunicationHistory;